// The one multi-value picker (Gio, 2026-10-17). Every list that picks several
// values from a catalog uses it: selected rows first and sorted, a search box,
// Clear and Select all shown, the keyboard, and the phone sheet.
// `multi-select.guard.test.ts` refuses a hand-rolled one in `src/screens`.
// Visual is .dd / .dd-trigger / .dd-pop / .dd-item from styles.css.

import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { ResponsivePicker } from './responsive-picker';
import { Icons } from './primitives';
import { useOutsideClick } from './dropdowns';
import { MULTI_MENU_MIN_WIDTH_PX, menuPosition, menuVertical } from './dropdown-geometry';
import type { MenuPosition, MenuVertical } from './dropdown-geometry';

/** Tallest the open menu grows, when the viewport has the room. */
const MENU_MAX_HEIGHT_PX = 400;
import { orderSelectedFirst } from '@/lib/selection-order';
import { usePhone } from '@/lib/responsive';
import type { SelectionOrder } from '@/lib/selection-order';

export interface MultiSelectOption<T extends string = string> {
  id: T;
  label: string;
  sublabel?: string;
  meta?: string;
  dot?: string;
  swatch?: string;
  /** Always selected: shown with a lock instead of a checkbox, listed first,
   *  kept by Clear, and part of every value this control emits. */
  locked?: boolean;
}

export interface MultiSelectProps<T extends string = string> {
  ariaLabel?: string;
  value?: readonly T[];
  options: readonly MultiSelectOption<T>[];
  onChange: (value: T[]) => void;
  /** `alpha` sorts each group by label; `ranked` keeps `options`' order (age
   *  bands, credit score: low to high). Every call site decides. */
  order: SelectionOrder;
  placeholder?: string;
  searchPlaceholder?: string;
  width?: string | number;
  popWidth?: number | null;
  size?: 'sm' | 'md';
  triggerClassName?: string;
  disabled?: boolean;
}

export function MultiSelect<T extends string = string>({
  ariaLabel = undefined,
  value = [],
  options,
  onChange,
  order,
  placeholder = '— Select —',
  searchPlaceholder = 'Search…',
  width = '100%',
  popWidth = null,
  size = 'md',
  triggerClassName = '',
  disabled = false,
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const phone = usePhone();
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<MenuPosition>({ top: 0, left: 0, width: 0 });
  const [vertical, setVertical] = useState<MenuVertical>({ top: 0, maxHeight: MENU_MAX_HEIGHT_PX });
  // The selection as it was when the list opened or the search last changed.
  // Rows are ordered by this, never by the live selection, so a ticked row
  // stays under the cursor (Gio, 2026-10-17: a row jumping away reads as a
  // failed click).
  const [pinned, setPinned] = useState<{ version: number; ids: ReadonlySet<string> }>(
    () => ({ version: 0, ids: new Set() }),
  );
  // The row order itself is frozen with it: a label refetched while the list is
  // open (a renamed production) must not move its row either. Only a change in
  // which rows exist computes the order again.
  const orderRef = useRef<{ key: string; ids: readonly string[] }>({ key: '', ids: [] });
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  useOutsideClick(wrapRef, () => setOpen(false));

  const lockedIds = options.filter((option) => option.locked).map((option) => option.id);
  const selectedIds: readonly T[] = Array.isArray(value) ? value : [];
  const selectedSet = new Set<T>([...selectedIds, ...lockedIds]);
  // The value keeps today's order: what was there, then what was added. A
  // stored list (an audience's production ids) never reorders, so it never
  // reads as an edit.
  const emit = (next: readonly T[]) => {
    const missingLocked = lockedIds.filter((id) => !next.includes(id));
    onChange([...next, ...missingLocked]);
  };
  const selectedOptions = [...selectedSet]
    .map((id) => options.find((option) => option.id === id))
    .filter((option): option is MultiSelectOption<T> => option !== undefined);
  const count = selectedSet.size;
  const triggerLabel = count === 0
    ? placeholder
    : count === 1
      ? selectedOptions[0]?.label || [...selectedSet][0]
      : `${count} selected`;

  const close = () => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  const openIt = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos(menuPosition(
        rect,
        popWidth ?? undefined,
        window.innerWidth,
        { minWidth: MULTI_MENU_MIN_WIDTH_PX },
      ));
      setVertical(menuVertical(rect, window.innerHeight, MENU_MAX_HEIGHT_PX));
      setPinned((current) => ({ version: current.version + 1, ids: new Set(selectedSet) }));
      setQ('');
      setActive(0);
      window.setTimeout(() => searchRef.current?.focus(), 20);
    }
    setOpen((current) => !current);
  };
  const search = (next: string) => {
    setQ(next);
    setPinned((current) => ({ version: current.version + 1, ids: new Set(selectedSet) }));
    setActive(0);
  };

  // Which rows exist, not the order they arrive in: a refetch that re-sorts a
  // renamed row must not count as a new list.
  const membership = options.map((option) => option.id as string).sort().join('\u0000');
  const orderKey = `${pinned.version}\u0000${order}\u0000${membership}`;
  if (orderRef.current.key !== orderKey) {
    const ordered = orderSelectedFirst(
      options,
      pinned.ids,
      order,
      (option) => option.id,
      (option) => option.label,
    );
    orderRef.current = {
      key: orderKey,
      ids: [
        ...ordered.filter((option) => option.locked),
        ...ordered.filter((option) => !option.locked),
      ].map((option) => option.id),
    };
  }
  const byId = new Map(options.map((option) => [option.id as string, option]));
  const rows = orderRef.current.ids
    .map((id) => byId.get(id))
    .filter((option): option is MultiSelectOption<T> => option !== undefined);
  const needle = q.trim().toLowerCase();
  const filtered = !needle
    ? rows
    : rows.filter((option) => {
        const hay = `${option.label || ''} ${option.sublabel || ''} ${option.meta || ''}`.toLowerCase();
        return hay.includes(needle);
      });
  const unselectedShown = filtered.filter((option) => !selectedSet.has(option.id));

  const toggle = (option: MultiSelectOption<T>) => {
    if (option.locked) return;
    emit(selectedSet.has(option.id)
      ? selectedIds.filter((selectedId) => selectedId !== option.id)
      : [...selectedIds, option.id]);
  };
  const focusRow = (index: number) => {
    const clamped = Math.max(0, Math.min(index, filtered.length - 1));
    setActive(clamped);
    rowRefs.current[clamped]?.focus();
  };
  const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    const option = filtered[index];
    if (!option) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(option);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) searchRef.current?.focus();
      else focusRow(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusRow(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusRow(filtered.length - 1);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      // Typing goes to the search box, as in a native listbox.
      event.preventDefault();
      search(q + event.key);
      searchRef.current?.focus();
    }
  };

  const padY = size === 'sm' ? '4px 8px' : '6px 10px';
  const fontSize = size === 'sm' ? 11.5 : 12;
  const selectedTitle = selectedOptions.map((option) => option.label).join(', ');
  const activeIndex = Math.min(active, Math.max(0, filtered.length - 1));

  return (
    <div className="dd" ref={wrapRef} style={{ display: 'block', width }}>
      <button
        ref={triggerRef}
        className={`dd-trigger ${open ? 'open' : ''} ${triggerClassName}`}
        onClick={openIt}
        style={{ width: '100%', padding: padY, fontSize }}
        type="button"
        disabled={disabled}
        aria-disabled={disabled || undefined}
        title={selectedTitle || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className="val"
          style={{
            color: count ? 'var(--ink)' : 'var(--ink-3)',
            fontWeight: count ? 600 : 500,
          }}
        >
          {triggerLabel}
        </span>
        {count > 1 && (
          <span className="dd-selected-count" aria-hidden="true">{count}</span>
        )}
        <span className="chev">{Icons.chev_d}</span>
      </button>
      {open && (
        <ResponsivePicker label={ariaLabel || 'Choose options'} onClose={() => setOpen(false)}><div
          className="dd-pop dd-multi-pop"
          onKeyDown={(event) => {
            // Escape anywhere in the picker (search, a row, a footer button)
            // closes the picker alone, never the drawer it sits in.
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
          style={{
            // On a phone the picker is a static sheet (`.control-picker > .dd-pop`)
            // whose height the stylesheet sizes to the viewport; a cap measured
            // from the trigger would only squeeze it.
            ...(phone ? {} : {
              top: vertical.top ?? 'auto',
              bottom: vertical.bottom ?? 'auto',
              maxHeight: vertical.maxHeight,
            }),
            left: pos.left,
            width: pos.width,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="dd-search-row">
            <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>{Icons.search}</span>
            <input
              ref={searchRef}
              className="dd-search-input"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={q}
              onChange={(event) => search(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' && filtered.length > 0) {
                  event.preventDefault();
                  focusRow(0);
                }
              }}
            />
            {q && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  search('');
                  searchRef.current?.focus();
                }}
                type="button"
                aria-label="Clear search"
              >
                {Icons.x}
              </button>
            )}
          </div>
          <div
            className="dd-multi-options"
            role="listbox"
            aria-label={ariaLabel || 'Options'}
            aria-multiselectable="true"
          >
            {filtered.length === 0 ? (
              <div className="muted dd-empty">
                No matches for <b>"{q}"</b>.
              </div>
            ) : (
              filtered.map((option, index) => {
                const isSelected = selectedSet.has(option.id);
                return (
                  <div
                    key={option.id}
                    ref={(element) => { rowRefs.current[index] = element; }}
                    className={`dd-item ${isSelected ? 'on' : ''}${option.locked ? ' is-locked' : ''}`}
                    onClick={() => { setActive(index); toggle(option); }}
                    onKeyDown={(event) => onRowKeyDown(event, index)}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.locked || undefined}
                    tabIndex={index === activeIndex ? 0 : -1}
                  >
                    {option.dot && <span className="dot" style={{ background: option.dot }}/>}
                    {option.swatch && <span style={{ width: 10, height: 10, borderRadius: 2, background: option.swatch, flexShrink: 0 }}/>}
                    <div className="dd-option-copy">
                      <span className="dd-option-label">{option.label}</span>
                      {option.sublabel && <span className="muted dd-option-sublabel">{option.sublabel}</span>}
                    </div>
                    {option.meta && <span className="muted mono dd-option-meta">{option.meta}</span>}
                    {option.locked ? (
                      <span className="dd-multi-lock" title="Always included" aria-hidden="true">{Icons.lock}</span>
                    ) : (
                      <span className={`dd-multi-check ${isSelected ? 'on' : ''}`} aria-hidden="true">
                        {isSelected ? Icons.check : ''}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="dd-multi-foot">
            <div className="dd-multi-foot-actions">
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={selectedIds.every((id) => lockedIds.includes(id))}
                onClick={() => emit([])}
              >
                Clear
              </button>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={unselectedShown.length === 0}
                onClick={() => emit([...selectedIds, ...unselectedShown.map((option) => option.id)])}
              >
                Select all shown
              </button>
            </div>
            <span className="muted mono">
              {count} selected
            </span>
            <button className="btn btn-primary btn-sm" type="button" onClick={close}>
              Done
            </button>
          </div>
        </div></ResponsivePicker>
      )}
    </div>
  );
}
